import * as XLSX from 'xlsx';
import MainCard from 'components/MainCard';
import { Empty, Flex, Tooltip } from 'antd';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import LynIcon from 'components/@extended/LynIcon';
import { useDispatch, useSelector } from 'react-redux';
import { tableFontSize } from 'components/StyleConstant';
import ColumnFilter from 'components/@extended/ColumnFilter';
import React, { useCallback, useEffect, useMemo } from 'react';
import SortingIconGroup from 'components/@extended/SortingIconGroup';
import TablePagination from 'components/LynDataTable/DataTablePagination';
import SelectVisibleColumn from 'components/@extended/SelectVisibleColumn';
import { setDataTableInfo, setScrollIntoTableRow } from 'store/reducer/mainSlice';
import { useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { Box, Button, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import ExportButton from 'components/button/ExportButton';

const stickyBody = {
	left: 0,
	position: 'sticky',
	borderBottom: '1px solid #ffffff',
	borderRight: '1px solid #ffffff',
	borderColor: '#ffffff',
};

const stickyHead = {
	left: 0,
	py: 1.5,
	zIndex: 3,
	position: 'sticky',
	color: '#ffffff',
	background: 'white',
	bgcolor: '#526375',
};

const stickySearch = {
	p: 0.5,
	left: 0,
	zIndex: 3,
	position: 'sticky',
	bgcolor: '#ebebeb',
};

const LeaseV9NewDataTable = ({
	children,
	DATA = [],
	current = 0,
	COLUMNS = [],
	rowSelection,
	deleteHandle,
	pageSize = 15,
	onClickHandler,
	bulkDelete = true,
	pagination = true,
	activeDeactiveHandle,
	firstColStrick = true,
	xlsxFileName = 'lynorg',
	exportButtonShow = true,
	defaultVisibleColumn = [],
	setRowSelection = () => {},
	rowIndexColorMapping = {},
	columnFilterSelector = true,
	tableUpperLeftExtraInfo = null,
}) => {
	const theme = useTheme();
	const location = useLocation();
	const dispatch = useDispatch();

	const { pathname } = location;

	const limit = useSelector((state) => state.main.limit);
	const pageIndex = useSelector((state) => state.main.page);
	const clickedRow = useSelector((state) => state.main.clickedItem);

	const data = useMemo(() => DATA ?? [], [DATA]);
	const columns = useMemo(() => COLUMNS, [COLUMNS]);

	const defaultColumn = useMemo(() => {
		return {
			Filter: ColumnFilter,
			sortDescFirst: true,
		};
	}, []);

	const tableInstance = useTable(
		{
			columns,
			data: data ?? [],
			defaultColumn,
			initialState: { pageIndex: current, pageSize: pageSize, hiddenColumns: defaultVisibleColumn },
			autoResetPage: true,
		},
		useFilters,
		useSortBy,
		usePagination,
		useRowSelect
	);

	const {
		rows,
		page,
		state,
		gotoPage,
		allColumns,
		prepareRow,
		setPageSize,
		filteredRows,
		headerGroups,
		getTableProps,
		selectedFlatRows,
		getTableBodyProps,
		getToggleHideAllColumnsProps,
	} = tableInstance;

	function generateFileName(xlsxFileReName) {
		const pattern = /^(\d+)_date$/i;

		if (pattern.test(xlsxFileReName)) {
			const match = xlsxFileReName.match(pattern);
			const numberPart = match[1];

			const today = new Date();
			const year = today.getFullYear();
			const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
			const day = String(today.getDate()).padStart(2, '0');

			const formattedDate = `${year}.${month}.${day}`;

			return `${numberPart}_${formattedDate}`;
		}

		return xlsxFileReName;
	}

	const dataExport = useCallback(() => {
		const headerArr = headerGroups[0].headers.map((item) => item.Header).filter((item) => item !== 'Action' && typeof item !== 'function');

		const rowsArr = rows.map((item) => {
			const disableColumnKey = [...state.hiddenColumns, 'Selection', 'Action'];
			const obj = { ...item.values };
			disableColumnKey.map((key) => {
				delete obj[key];
				return delete obj[key];
			});
			return Object.values(obj);
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);

		XLSX.utils.sheet_add_aoa(ws, [headerArr]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateFileName(xlsxFileName)}.xlsx`);
	}, [state.hiddenColumns, generateFileName]);

	const memorizeClickedItem = (id) => {
		dispatch(setScrollIntoTableRow({ clickedItem: { [pathname]: id } }));
		dispatch(setDataTableInfo({ page: { [pathname]: state?.pageIndex }, limit: { [pathname]: state?.pageSize } }));
	};

	useEffect(() => {
		setRowSelection(selectedFlatRows);
	}, [selectedFlatRows]);

	useEffect(() => {
		if (state.filters.length === 0) {
			gotoPage(pageIndex?.[pathname] || state?.pageIndex);
		}
		if (pagination) {
			setPageSize(limit?.[pathname] || state?.pageSize);
		}
	}, []);

	useEffect(() => {
		const id = clickedRow[pathname];

		if (id) {
			const project = document.getElementById(id);

			if (project) {
				project.classList.add('active');
				project.scrollIntoView({ behavior: 'smooth', block: 'center' });

				dispatch(setScrollIntoTableRow({ clickedItem: {} }));
			}
		}
	}, [allColumns, state.pageIndex, state.pageSize]);

	return (
		<>
			{tableUpperLeftExtraInfo && (
				<Box>
					{columnFilterSelector && tableUpperLeftExtraInfo && (
						<Box>
							{Object.keys(tableUpperLeftExtraInfo || {}).map((key) => (
								<Box key={key} sx={{ display: 'flex' }}>
									<Typography sx={{ width: '100px', fontWeight: 'bold' }}>
										{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
									</Typography>
									<Typography sx={{ width: '30px' }}>: </Typography>
									<Typography>{tableUpperLeftExtraInfo?.[key]}</Typography>
								</Box>
							))}
						</Box>
					)}
				</Box>
			)}

			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 1, sm: 2 } }}>
				<Box>
					{columnFilterSelector ? (
						<SelectVisibleColumn
							getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
							allColumns={allColumns}
							visibleColumns={headerGroups[0]?.headers}
						/>
					) : (
						children
					)}
				</Box>

				<Flex justify="space-between" align="center" gap=".5rem">
					{rowSelection?.length > 0 && (
						<>
							<div>
								<b style={{ fontSize: '14px' }}>{rowSelection?.length}</b> {rowSelection?.length > 1 ? 'rows' : 'row'} selected
							</div>

							<Tooltip title={rowSelection?.length > 1 ? 'Activate all' : 'Activate'}>
								<Button
									type="primary"
									style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.success, color: 'white' }}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Inactive');
									}}
								>
									{rowSelection?.length > 1 ? 'Activate all' : 'Activate'}
								</Button>
							</Tooltip>

							<Tooltip title={rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
								<Button
									type="primary"
									style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.submit, color: 'white' }}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Active');
									}}
								>
									{rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}
								</Button>
							</Tooltip>

							{bulkDelete && (
								<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
									<Button
										type="primary"
										style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.delete, color: 'white' }}
										onClick={(e) => {
											e.stopPropagation();

											if (typeof deleteHandle === 'function') {
												let arr = rowSelection?.map((item) => item?.original);
												deleteHandle(arr);
											}
										}}
									>
										{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
									</Button>
								</Tooltip>
							)}
						</>
					)}

					{exportButtonShow && (
						<ExportButton handleDataExport={dataExport} />

						// <Button
						// 	disableRipple
						// 	color="secondary"
						// 	onClick={dataExport}
						// 	aria-haspopup="true"
						// 	aria-label="open profile"
						// 	sx={{ padding: '8px 12px', color: 'text.primary', bgcolor: 'common.white', border: `1px solid ${theme.palette.border}` }}
						// >
						// 	<Stack direction="row" gap={1}>
						// 		<LynIcon folder="icons" fileName={'export'} format="svg" sx={{ marginRight: '4px' }} />{' '}
						// 		<Typography style={{ mt: '12px' }}>Export</Typography>
						// 	</Stack>
						// </Button>
					)}
				</Flex>
			</Box>

			<MainCard sx={{ width: '100%', mt: 2, borderColor: '#526375' }} content={false}>
				<TableContainer
					sx={{
						width: '100%',
						display: 'block',
						overflowX: 'auto',
						position: 'relative',
						mb: pagination ? '36px' : '0px',
						maxHeight: `calc(100svh - 330px)`,

						'& td, & th': { whiteSpace: 'nowrap' },
					}}
				>
					<Table
						stickyHeader
						{...getTableProps()}
						sx={{
							'& th:not(:last-of-type)': { borderRight: 1, borderColor: 'common.white' },
						}}
					>
						<TableHead>
							{headerGroups?.map((headerGroup, index) => (
								<TableRow
									{...headerGroup?.getHeaderGroupProps()}
									sx={{ height: '50px', th: { borderBottom: 'none', userSelect: 'none' } }}
									key={index}
								>
									{headerGroup?.headers.map((column, index) => (
										<TableCell
											{...column?.getHeaderProps()}
											sx={
												firstColStrick && index === 0
													? {
															...stickyHead,
															width: column?.width || 'auto',
															fontSize: tableFontSize.headerSize,
															fontWeight: tableFontSize.headerWeight,
														}
													: {
															py: 1.5,
															color: '#ffffff',
															backgroundColor: '#526375',
															width: column?.width || 'auto',
														}
											}
											key={index}
										>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													fontSize: tableFontSize.headerSize,
													fontWeight: tableFontSize.headerWeight,
													justifyContent: column?.align ? column?.align : 'space-between',
												}}
												{...column.getSortByToggleProps()}
											>
												{column?.render('Header')}
												{column?.canSort && <SortingIconGroup sortingProps={column} />}
											</Box>
										</TableCell>
									))}
								</TableRow>
							))}

							{headerGroups?.map((headerGroup, index) => (
								<TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& .MuiTableCell-root': { top: '48px' } }} key={index}>
									{headerGroup.headers.map((column, nestedIndex) =>
										column?.id === 'Selection' || column?.id === 'Sl' ? (
											<TableCell
												sx={{
													backgroundColor: '#ebebeb',
													borderBottom: '1px solid #ffffff',
													...(nestedIndex === 0 && { position: 'sticky', left: 0, zIndex: '3' }),
												}}
												key={index + '_' + nestedIndex}
											></TableCell>
										) : (
											<TableCell
												sx={
													firstColStrick && nestedIndex === 0
														? stickySearch
														: {
																p: 0.5,
																backgroundColor: '#ebebeb',

																'&:first-of-type': { backgroundColor: '#ebebeb' },
																'&:last-child': { borderColor: 'white' },
															}
												}
												{...column.getHeaderProps()}
												key={index + '_' + nestedIndex}
											>
												{column.canFilter ? column.render('Filter') : null}
											</TableCell>
										)
									)}
								</TableRow>
							))}
						</TableHead>

						<TableBody {...getTableBodyProps()} key="table_body2">
							{page?.length > 0 ? (
								page?.map((row, index) => {
									prepareRow(row);

									return (
										<TableRow
											hover
											key={index}
											id={row.id}
											{...row?.getRowProps()}
											onClick={() => memorizeClickedItem(row.id)}
											sx={{
												cursor: 'pointer',
												backgroundColor:
													Object.keys(rowIndexColorMapping).length === 0
														? '#FCFCFC'
														: rowIndexColorMapping[index]?.rowColor,
												'& .MuiTableCell-root:first-of-type': {
													bgcolor:
														Object.keys(rowIndexColorMapping).length === 0
															? '#FCFCFC'
															: rowIndexColorMapping[index]?.rowColor,
													borderBottom: '1px solid #E0E0E0',
												},
												'&:hover .MuiTableCell-root:first-of-type': { bgcolor: '#f5f5f5' },

												'&.active .MuiTableCell-root:first-of-type': { bgcolor: '#ffffff' },
											}}
										>
											{row?.cells?.map((cell, index) => (
												<TableCell
													key={index}
													sx={index === 0 && firstColStrick ? stickyBody : { borderBottom: '1px solid #E0E0E0' }}
													{...cell.getCellProps()}
													onClick={() => {
														if (!row?.cells[index]?.column?.onClicked) {
															onClickHandler(row.original);
														}
													}}
												>
													{cell?.column?.id === 'Selection' || cell?.column?.id === 'Sl' ? (
														<Box
															component="div"
															sx={{
																textAlign: cell?.column?.cellAlign || 'left',
															}}
														>
															{cell.render('Cell')}
														</Box>
													) : (
														<Box
															component="div"
															sx={{
																display: 'block',
																maxWidth: '337px',
																overflow: 'hidden',
																whiteSpace: 'nowrap',
																textOverflow: 'ellipsis',
																fontSize: tableFontSize.cellSize,
																fontWeight: tableFontSize.cellWeight,
																textAlign: cell?.column?.cellAlign || 'left',
															}}
														>
															{cell.render('Cell')}
														</Box>
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={columns?.length}>
										<Box
											component="div"
											sx={{
												width: '100%',
												display: 'flex',
												height: '350px',
												alignItems: 'center',
												flexDirection: 'column',
												justifyContent: 'center',
											}}
										>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{pagination && (
					<Box sx={{ position: 'absolute', left: 0, bottom: 0, width: '100%', backgroundColor: '#526375' }}>
						<TablePagination
							sx={{
								'& .MuiTablePagination-root': {
									color: '#fff',
								},
							}}
							{...{
								pathname,
								state: state,
								gotoPage: gotoPage,
								setPageSize: setPageSize,
								count: filteredRows?.length,
							}}
						/>
					</Box>
				)}
			</MainCard>
		</>
	);
};

export default React.memo(LeaseV9NewDataTable);
